import React, { useEffect, useRef, useState } from "react";
import useTranslate from "../../core/languages/hooks/useTranslate";
import ClearDataButton from "../../common/components/filters/ClearDataButton";

export enum TransferStatusEnum {
    PENDING = 0,
    TRANSFER_CREATED = 1,
    UPDATED = 2,
    CANCELLED = 3,
    CONFIRMED = 4,
    REQUEST_INFO = 5,
    FORM_SUBMITTED = 6,
}

// Filter choices - Only 3 groups for filter
export const transferStatusFilterChoices = [
    { id: 'pending',
        name: 'common.enum.transferStatus.pending',
        statuses: [
            TransferStatusEnum.PENDING,
            TransferStatusEnum.TRANSFER_CREATED,
            TransferStatusEnum.UPDATED,
            TransferStatusEnum.REQUEST_INFO,
            TransferStatusEnum.FORM_SUBMITTED
        ] },
    { id: 'confirmed', name: 'common.enum.transferStatus.confirmed', statuses: [TransferStatusEnum.CONFIRMED] },
    { id: 'cancelled', name: 'common.enum.transferStatus.cancelled', statuses: [TransferStatusEnum.CANCELLED] },
];

// Select Filter - Para filtros sin formulario
export interface TransferStatusSelectProps {
    onChange: (updates: any) => void;
    value?: number;
}

export const TransferStatusSelect: React.FC<TransferStatusSelectProps> = ({
    onChange,
    value,
}) => {
    const t = useTranslate();
    const statusRef = useRef<HTMLDivElement | null>(null);
    const [showStatusPopover, setShowStatusPopover] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(value !== undefined ? String(value) : null);

    const handleSelectStatus = (filterKey: string | null) => {
        setSelectedStatus(filterKey);
        setShowStatusPopover(false);

        if (filterKey !== null) {
            const choice = transferStatusFilterChoices.find(c => c.id === filterKey);
            if (choice) {
                // Si solo hay un estado, enviar el número; si hay varios, enviar el array
                onChange({ status: choice.statuses.length === 1 ? choice.statuses[0] : choice.statuses });
            }
        } else {
            onChange({ status: undefined });
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (statusRef.current && !statusRef.current.contains(e.target as Node)) {
            setShowStatusPopover(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const getStatusLabel = () => {
        if (selectedStatus === null) {
            return t("transfers.fields.status");
        }
        const choice = transferStatusFilterChoices.find(c => c.id === selectedStatus);
        return choice ? t(choice.name) : t("transfers.fields.status");
    };

    return (
        <div
            ref={statusRef}
            className="relative w-60 cursor-pointer [ nc-hero-field-padding ] items-center min-w-0"
        >
            <div className="flex items-start">
                <div className="flex-grow min-w-0">
                    <div
                        className="w-full p-0 xl:text-lg font-semibold min-w-0 cursor-pointer text-neutral-800 dark:text-neutral-100"
                        onClick={() => setShowStatusPopover((v) => !v)}
                    >
                        <span className="whitespace-normal break-words block">
                            {getStatusLabel()}
                        </span>
                    </div>
                    <span className="block mt-0.5 text-sm text-neutral-400 font-light">
                        {t("transferSearch.message.searchByTransferStatus")}
                    </span>
                </div>

                {selectedStatus !== null && (
                    <ClearDataButton onClick={() => handleSelectStatus(null)} />
                )}
            </div>

            {showStatusPopover && (
                <div className="absolute left-0 z-40 w-full bg-white dark:bg-neutral-800 top-full mt-3 py-3 rounded-3xl shadow-xl max-h-60 overflow-y-auto">
                    {transferStatusFilterChoices.map((choice) => (
                        <div
                            key={choice.id}
                            className="flex px-4 sm:px-8 items-center py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                            onClick={() => handleSelectStatus(choice.id)}
                        >
                            <span className="block font-medium text-neutral-700 dark:text-neutral-200">
                                {t(choice.name)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default TransferStatusSelect;
